import config from '@/config'
import { createI18n } from 'vue-i18n'
import en from './locales/en.json'
import ru from './locales/ru.json'
import apiErrorsEn from './locales/api_errors.en.json'
import apiErrorsRu from './locales/api_errors.ru.json'
// Генерируются из общих PO-категорий ltms-backend (localization/<lang>/LC_MESSAGES/shared.po) плагином из i18n-po.mjs
import poEn from './generated/en.json'
import poRu from './generated/ru.json'
// Правила plural из Plural-Forms тех же PO: формы совпадают с backend-ngettext
import poPluralRules from './generated/plural-rules.js'
import { readStoredAppLocale } from './localeStorage.js'

// Разделитель контекста как в .mo у gettext; должен совпадать с CONTEXT_SEPARATOR в i18n-po.mjs
const CONTEXT_SEPARATOR = '\u0004'

/**
 * Ключ фразы с контекстом (`msgctxt`) из общего каталога backend `shared.po`.
 *
 * Контекст различает омонимы: «Открыть» — кнопка и «Открыть» — статус переводятся
 * по-разному. Использование: `t(contextKey('кнопка', 'Открыть'))`; для plural — как
 * обычно, `t(contextKey(ctx, msgid), params, n)`.
 *
 * @param {string} context контекст (`msgctxt`)
 * @param {string} msgid русская фраза
 * @returns {string} ключ vue-i18n
 */
export function contextKey(context, msgid) {
  return `${context}${CONTEXT_SEPARATOR}${msgid}`
}

export const i18n = createI18n({
  legacy: false,
  locale: readStoredAppLocale(),
  fallbackLocale: 'en',
  // PO подключается последним и перекрывает JSON: это целевой источник переводов,
  // JSON переносятся в PO постепенно (конфликты плагин показывает warnings).
  messages: {
    en: { ...en, api_errors: apiErrorsEn, ...poEn },
    ru: { ...ru, api_errors: apiErrorsRu, ...poRu },
  },
  // RU-сообщения с `|` — ровно 3 формы (one | few | many), EN — 2: правило gettext
  // не учитывает число веток
  pluralRules: poPluralRules,
  /**
   * Глобальные именованные форматы для `d()` в Composition API.
   * @see https://vue-i18n.intlify.dev/guide/advanced/composition.html#datetime-formatting
   */
  datetimeFormats: {
    ru: {
      print: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      },
    },
    en: {
      print: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      },
    },
  },
  /**
   * Глобальные именованные форматы для `n()` в Composition API.
   * @see https://vue-i18n.intlify.dev/guide/advanced/composition.html#number-formatting
   */
  numberFormats: {
    ru: {
      decimal2: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      integer: {
        style: 'decimal',
        maximumFractionDigits: 0,
      },
    },
    en: {
      decimal2: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      integer: {
        style: 'decimal',
        maximumFractionDigits: 0,
      },
    },
  },
  // В основном проверка идет через линтер, поэтому по умолчанию отключаем предупреждения
  missingWarn: config.VITE_LOCALE_MISSING_WARN === 'true' || false,
  fallbackWarn: config.VITE_LOCALE_FALLBACK_WARN === 'true' || false,
})

// Warnings генерации PO (нет файла, пустые переводы, конфликты с JSON) — в консоль браузера в dev
if (import.meta.env.DEV) {
  import('virtual:i18n-po-warnings').then(({ default: warnings }) => {
    warnings.forEach(message => console.warn(message))
  })
}
